<template>
	<dialog-slot
			:title="title"
			@updateClose="onClose"
			@updateSave="onSave"
			width="800px"
			apply-text="Send"
			:preloader="isPreloaderButtonGroups"
	>
		<div class="dialogGPT">
			<div class="head">
				<div class="hrow i2">
					<field
							:classError="$v.driver.id.$error"
							margin="0"
							label="Driver"
							placeholder="Select driver name"
							@updateAutocomplete="onAutocomplete($event, 'driver')"
							:autocompleteValue="driver"
							:items="filterDriverList"
							:isClearable="false"
							is-autocomplete
							is-required
					/>
					<field
							v-if="driver.id && customer.id != null"
							:classError="$v.customer.id.$error"
							margin="0"
							label="Customer"
							:setInputValue="customer.value"
							is-required
							is-disabled
							is-readonly
					/>
					<div v-if="!driver.id || $v.driver.id.$error || customer?.id == null" class="customerNull">
						<!-- 1. Если драйвер НЕ выбран -->
						<div v-if="!driver.id">
							🚚 <span class="text--secondary caption">
							Choose a driver to auto-fill the customer.
						</span>
						</div>

						<!-- 2. Если драйвер выбран, но не прошёл валидацию -->
						<div v-else-if="$v.driver.id.$error">
							⚠️ <span class="caption red--text">
								The customer field is required.<br>
								<b>Please select a driver linked to a customer.</b>
							</span>
						</div>

						<!-- 3. Если драйвер выбран, но customer пустой -->
						<div v-else-if="customer?.id == null">
							🚫 <span class="caption red--text">
								No customer is assigned to this driver.<br>
								<b> Either pick another driver or assign a customer to this driver.</b>
							</span>
						</div>
					</div>
				</div>
				<div class="hrow i3">
					<field
							:classError="$v.truck.id.$error"
							margin="0"
							label="Truck"
							placeholder="Select truck number"
							@updateAutocomplete="onAutocomplete($event, 'truck')"
							:autocompleteValue="truck"
							:items="trucksList"
							:isClearable="false"
							is-autocomplete
							is-required
							:is-disabled="!driver"
					/>
					<field
							:classError="$v.broker.id.$error"
							margin="0"
							label="Broker"
							placeholder="Select broker"
							@updateAutocomplete="onAutocomplete($event, 'broker')"
							:autocompleteValue="broker"
							:items="brokers"
							:isClearable="false"
							is-autocomplete
							is-required
							@updateSearchText="onSearchText($event, 'broker')"
							is-btn-autocomplete
							:closeMenuOnBtnClick="true"
					/>

					<field
							v-if="isBrokerMcLocked"
							:classError="$v.searchMc.$error"
							margin="0"
							label="Broker MC"
							:setInputValue="searchMc"
							is-disabled
							is-readonly
							is-required
					/>

					<field
							v-else
							:classError="$v.searchMc.$error"
							margin="0"
							label="Broker MC"
							placeholder="Enter Broker MC"
							:setInputValue="searchMc"
							@updateInput="onInputMc"
							is-required
					/>
				</div>

			</div>

			<div class="main">
				<div class="fileUploadWrap">
					<div class="file-upload-section">
						<!-- Drop zone -->
						<div
								v-if="!file.value"
								class="drop-zone"
								@dragover.prevent
								@dragenter.prevent
								@drop.prevent="handleFileDrop"
								@click="$refs.fileInput.click()"
						>
							<input
									ref="fileInput"
									type="file"
									accept="application/pdf"
									@change="handleFileSelect"
									style="display: none"
							/>
							<span>DrugDrop file</span>
						</div>

						<!-- Uploaded file -->
						<div v-else class="uploaded-file-box">
							<div class="text">📄 {{ file.name }}</div>
							<v-btn @click="clearFile" class="remove-btn" icon color="green">
								<v-icon>mdi-trash-can-outline</v-icon>
							</v-btn>
						</div>
					</div>
					<div v-if="fileError" class="mt-1">
						❗ <span class="caption red--text">{{ fileError }}</span>
					</div>

					<div v-if="$v.file.value.$error" class="mt-1">
						❗ <span class="caption red--text">Please make sure to add a file before proceeding.</span>
					</div>
				</div>

				<div class="sendStatusWrap">
					<div class="statusLineWrap">
						<div class="title">{{ textCompletionStatusTitle }} <span class="proc" :class="{ active: isAutoSending }"> {{ Math.floor(parseFloat(lineBg)) + '%' }}</span></div>
						<div class="line">
							<div class="lineBg" :class="{ active: isAutoSending }" :style="{ width: lineBg }"></div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</dialog-slot>
</template>

<script>
import DialogSlot from "@/shared/DialogSlot/DialogSlot.vue";
import { mapActions, mapGetters } from "vuex";
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

export default {
	name: "DialogGPT",
	components: { DialogSlot },
	mixins: [ validationMixin ],

	validations() {
		return {
			file: { value: { required } },
			driver: { id: { required } },
			broker: { id: { required } },
			truck: { id: { required } },
			customer: { id: { required } },
			searchMc: { required }
		};
	},

	props: {
		flag: String,
		title: String,
		headers: Array,
		items: Array,
		filterDriverList: Array,
		filterTruckList: Array,
		brokers: Array,
		createdBroker: Object,
	},

	data() {
		return {
			textCompletionStatusTitle: '📊 Completion Status',
			textCompletionStatus: '📊 Completion Status',
			textCompletionStatusMod: '⚙️ Processing Data',
			lineBg: '1%',
			file: { name: '', value: '' },
			driver: { id: null, value: null },
			broker: { id: null, value: null },
			truck: { id: null, value: null },
			customer: { id: null, value: null },
			lineAnimationFrameId: null,
			isLineBgFinalized: false,
			isPreloaderButtonGroups: false,
			isAutoSending: false,
			trucksList: [],
			fileError: '',
			searchMc: '',
		};
	},

	computed: {
		...mapGetters([ "tripDialogConnections", "extractGPT" ]),

		rowLineBg() {
			const driverValid = this.driver.id;
			const brokerValid = this.broker.id;
			const truckValid = this.truck.id;
			const fileValid = this.file.value;
			const customerValid = this.customer.id;
			const validCount = [ driverValid, brokerValid, truckValid, fileValid, customerValid ].filter(Boolean).length;

			if (validCount === 5) return '26%';
			if (validCount === 4) return '22%';
			if (validCount === 3) return '18%';
			if (validCount === 2) return '12%';
			if (validCount === 1) return '6%';
			return '0%';
		},

		isFormComplete() {
			return Boolean(this.driver.id && this.broker.id && this.truck.id && this.customer.id && this.file.value);
		},

		selectedBrokerItem() {
			if (!this.broker || !this.broker.id || !Array.isArray(this.brokers)) return null;

			return this.brokers.find((item) => item.id === this.broker.id) || null;
		},

		brokerMcFromList() {
			if (!this.selectedBrokerItem) return '';

			return this.selectedBrokerItem.mc ? String(this.selectedBrokerItem.mc).trim() : '';
		},

		isBrokerMcLocked() {
			return Boolean(this.brokerMcFromList);
		}
	},

	watch: {
		driver: {
			handler() {
				this.lineBg = this.rowLineBg;
				this.tryAutoSend(); // [AUTO-SEND]
			},
			deep: true
		},
		broker: {
			handler() {
				this.lineBg = this.rowLineBg;
				this.tryAutoSend(); // [AUTO-SEND]
			},
			deep: true
		},
		truck: {
			handler() {
				this.lineBg = this.rowLineBg;
				this.tryAutoSend(); // [AUTO-SEND]
			},
			deep: true
		},
		customer: {
			handler() {
				this.lineBg = this.rowLineBg;
				this.tryAutoSend(); // [AUTO-SEND]
			},
			deep: true
		},
		'file.value'() {
			this.lineBg = this.rowLineBg;
			this.tryAutoSend(); // [AUTO-SEND]
		},
		extractGPT(val) {
			if (val && Object.keys(val).length) {
				this.animateLineBgToComplete(); // перейти на 100%
				// this.isPreloaderButtonGroups = false;
				// this.isAutoSending = false;
			}
		},
		createdBroker: {
			handler(val) {
				if (!val) return;

				this.broker = {
					id: val.id,
					value: val.value
				};

				this.searchMc = val.mc ? String(val.mc).trim() : '';

				this.$v.broker.id.$touch();
				this.$v.searchMc.$touch();
			},
			deep: true,
			immediate: true
		}
	},

	methods: {
		...mapActions([ "getTripDialogConnections", "getExtractGPT", "updateConnections" ]),
		onSearchText(name, key) {
			this.$emit('updateSearchText', { name, key, mc: this.searchMc } );
		},
		onInput({ value }, key, parent) {
			this.$emit('updateInput', { value, key, parent });
		},
		onInputMc({ value }) {
			this.searchMc = value;
			this.$v.searchMc.$touch();
		},
		onClose(type) {
			this.$emit('updateClose', type);
		},
		endAnimate() {
			// [AUTO-SEND] під кінець сценарію теж скинемо блокування
			this.isAutoSending = false;
			this.onClose('endAnimate');
		},

		async onSave() {
			this.$v.$touch();
			if (!this.$v.$error) {
				this.animateLineBgToFull(); // 🔥 запуск анимации
				this.textCompletionStatusTitle = this.textCompletionStatusMod;
				this.isPreloaderButtonGroups = true;

				const value = {
					file: this.file.value,
					driver_id: this.driver.id,
					broker_id: this.broker.id,
					broker_name: this.broker.value,
					mc: this.searchMc,
					truck_id: this.truck.id,
					customer_id: this.customer.id
				};

				this.$emit('updateSaveValue', this.file);

				await this.getExtractGPT(value);
			}
		},
		// [AUTO-SEND] єдине місце, де приймається рішення автозапускати onSave
		tryAutoSend() {
			// вже відправляємо або прелоадер активний — нічого не робимо
			if (this.isAutoSending || this.isPreloaderButtonGroups) return;

			// всі поля заповнені — запускаємо один раз
			if (this.isFormComplete) {
				this.isAutoSending = true;
				this.onSave();
			}
		},

		animateLineBgToComplete() {
			this.isLineBgFinalized = true;
			if (this.lineAnimationFrameId) {
				cancelAnimationFrame(this.lineAnimationFrameId);
				this.lineAnimationFrameId = null;
			}

			const target = 100;
			const duration = 2000;
			const start = performance.now();
			const initial = parseFloat(this.lineBg);

			const animate = (time) => {
				const elapsed = time - start;
				const progress = Math.min(elapsed / duration, 1);
				const newWidth = initial + ((target - initial) * progress);

				this.lineBg = `${ newWidth.toFixed(2) }%`;

				if (progress < 1) {
					this.lineAnimationFrameId = requestAnimationFrame(animate);
				} else {
					this.endAnimate();
				}
			};

			this.lineAnimationFrameId = requestAnimationFrame(animate);
		},

		animateLineBgToFull() {
			const target = 97;
			const duration = 10000;
			const start = performance.now();
			const initial = parseFloat(this.lineBg);
			this.isLineBgFinalized = false;

			const animate = (time) => {
				const elapsed = time - start;
				const progress = Math.min(elapsed / duration, 1);
				const newWidth = initial + ((target - initial) * progress);

				this.lineBg = `${ newWidth.toFixed(2) }%`;

				if (progress < 1 && !this.isLineBgFinalized) {
					this.lineAnimationFrameId = requestAnimationFrame(animate);
				}
			};

			this.lineAnimationFrameId = requestAnimationFrame(animate);
		},

		onAutocomplete({ value }, key) {
			this[key] = value;

			if (key === 'driver') {
				this.dialogConnections({ trip_id: false, [key]: value });
				this.customer = value.customer !== null ? { id: value.id, value: value.customer } : { id: null, value: null };
				this.trucksList = this.filterTruckList.filter((item) => item.customer === this.customer.value);
			}

			if (key === 'broker') {
				this.searchMc = value && value.mc ? String(value.mc).trim() : '';
				this.$v.broker.id.$touch();
				this.$v.searchMc.$touch();
			}

			this.$emit('updateAutocomplete', { value }, key);
		},

		async dialogConnections(params) {
			await this.getTripDialogConnections(params);
			await this.updateConnections();
		},

		updateConnections() {
			const { truck } = this.tripDialogConnections;
			this.truck = truck !== null ? truck : { id: null, value: null };
		},

		handleFileDrop(e) {
			const files = e.dataTransfer.files;
			if (files.length) this.processFile(files[0]);
		},
		handleFileSelect(e) {
			const files = e.target.files;
			if (files.length) this.processFile(files[0]);
		},
		/**
		 * Проверяет файл и, если валиден, читает его как DataURL.
		 * @param {File} file - Файл, который пользователь выбрал или перетащил.
		 * @returns {void} Ничего не возвращает.
		 */
		processFile(file) {
			const { ok, error } = this.validateDropFile(file, {
				// расширь список символов при необходимости
				forbidden: /[@#%&!<>:"/\\|?*]/,
				accept: [ 'application/pdf' ],
				maxSizeMB: 20
			});

			if (!ok) {
				this.fileError = error;
				this.file = { name: '', value: '' };
				return;
			}

			this.fileError = '';
			this.file = { name: 'Uploading...', value: '' };

			const reader = new FileReader();
			reader.onload = () => {
				this.file = { name: file.name, value: reader.result };
			};
			reader.readAsDataURL(file);
		},
		/**
		 * Локальная валидация имени, типа и размера файла.
		 * @param {File} file - Файл для проверки.
		 * @param {ValidateOpts} [opts] - Опции валидации.
		 * @returns {ValidateResult} Результат проверки.
		 */
		validateDropFile(file, opts = {}) {
			const forbidden = opts.forbidden instanceof RegExp ? opts.forbidden : /[@#%&!]/;
			const accept = Array.isArray(opts.accept) && opts.accept.length ? opts.accept : null;
			const maxSizeMB = typeof opts.maxSizeMB === 'number' ? opts.maxSizeMB : 20;

			if (!file) return { ok: false, error: 'No file provided.' };

			if (forbidden.test(file.name)) {
				return { ok: false, error: 'Filename must not contain: @ # % & ! < > : " / \\ | ? *' };
			}

			if (accept && !accept.includes(file.type)) {
				return { ok: false, error: `Unsupported file type: ${ file.type || 'unknown' }. Allowed: ${ accept.join(', ') }` };
			}

			const sizeMB = file.size / (1024 * 1024);
			if (sizeMB > maxSizeMB) {
				return { ok: false, error: `File is too large: ${ sizeMB.toFixed(2) } MB (limit ${ maxSizeMB } MB).` };
			}

			return { ok: true, error: '' };
		},
		clearFile() {
			this.file = { name: '', value: '' };
			this.fileError = '';
		}

	},

	mounted() {
		this.textCompletionStatusTitle = this.textCompletionStatus;
		this.lineBg = this.rowLineBg;
		this.file = { name: '', value: '' };
		this.driver = { id: null, value: null };
		this.truck = { id: null, value: null };
		this.customer = { id: null, value: null };
		this.isPreloaderButtonGroups = false;
		this.isAutoSending = false;
		this.trucksList = this.filterTruckList;
		this.searchMc = '';
	}
};
</script>

<style scoped lang="scss">
.dialogGPT {
	.head {
		display: grid;
		grid-template-columns: 1fr;
		grid-gap: 10px;
		.customerNull {
			display: flex;
			align-items: center;
		}
		.hrow {
			display: grid;
			grid-template-columns: 1fr;
			grid-gap: 10px;
			@media screen and (max-width: 768px) {
				grid-template-columns: 1fr;
			}
			&.i2 {
				grid-template-columns: 1fr 1fr;
				@media screen and (max-width: 768px) {
					grid-template-columns: 1fr;
				}
			}
			&.i3 {
				grid-template-columns: 1fr 1fr 1fr;
				@media screen and (max-width: 768px) {
					grid-template-columns: 1fr;
				}
			}
		}
	}
	.main {
		display: grid;
		grid-template-columns: 1fr;
		grid-gap: 20px;

		.file-upload-section {
			margin-top: 20px;
		}
		.drop-zone {
			border: 3px solid var(--card-bg-opacity);
			border-radius: 4px;
			padding: 30px;
			text-align: center;
			color: var(--card-color);
			cursor: pointer;
			font-size: 18px;
			transition: background 0.2s ease;
			user-select: none;
			&:hover {
				background: var(--card-bg-opacity);
			}
		}
		.uploaded-file-box {
			position: relative;
			display: flex;
			gap: 10px;
			justify-content: space-between;
			align-items: center;
			border: 2px dashed var(--base-green);
			padding: 20px;
			border-radius: 4px;
			color: var(--card-color);
			font-size: 16px;
			text-align: center;
			background: var(--card-bg-opacity);
			.text {
				display: flex;
				align-items: center;
			}
			.remove-btn {
				background: none;
				border: none;
				cursor: pointer;
				font-size: 18px;
			}
		}
		.sendStatusWrap {
			display: flex;
			align-items: center;
			width: 100%;
			.statusLineWrap {
				width: 100%;
				.title {
					text-align: center;
					margin-bottom: 10px;
				}
				.proc {
					color: var(--base-orange);
					&.active {
						color: var(--base-green);
					}
				}
				.line {
					width: 100%;
					display: block;
					height: 24px;
					position: relative;
					z-index: 10;
					border: 2px solid var(--card-bg-opacity);
					border-radius: 4px;
					.lineBg {
						transition: width 0.3s ease;
						position: absolute;
						z-index: 15;
						background: var(--base-orange);
						width: 1%;
						height: 100%;
						left: 0;
						top: 0;
						border-radius: 4px;
						overflow: hidden;
						&.active {
							background: var(--base-green);

						}
					}
				}
			}
		}
	}
}
</style>
